import { useEffect } from 'react';
import NextLink from 'next/link';
import { useOptimizely, Experiment, EventName } from 'lib/sdks/optimizely';
import { GlobalFooter, socialLinks } from '@checkatrade/web-ui';
import { type OnTrackEventArgs } from 'containers/AppContainer/AppContainer.interfaces';
import { EventMap } from '../../lib/sdks/analytics/event-map';

type FooterLink = {
  label: string;
  url: string;
  isExternal?: boolean;
  disableNextRouting?: boolean;
};

const footerLinks = [
  {
    url: '/information-centre',
    label: 'Information Centre',
    disableNextRouting: true,
  },
  {
    label: 'Terms and Conditions',
    url: '/checkatrade-terms-and-conditions-hub',
    disableNextRouting: true,
  },
  {
    url: '/privacy',
    label: 'Privacy',
  },
  {
    label: 'Contact Us',
    url: 'https://support.checkatrade.com/',
    isExternal: true,
  },
  {
    url: '/sitemap',
    label: 'Sitemap',
  },
] satisfies FooterLink[];

type FooterProps = {
  onTrackEvent: (event: OnTrackEventArgs) => void;
};
const isValidEvent = (
  eventName: string,
): eventName is OnTrackEventArgs['key'] => {
  return Object.keys(EventMap).includes(eventName);
};

const NextLinkWithoutPrefetch = (
  props: React.ComponentProps<typeof NextLink>,
) => <NextLink {...props} prefetch={false} />;
NextLinkWithoutPrefetch.$$typeof = NextLink.$$typeof;

NextLinkWithoutPrefetch.displayName = 'NextLinkWithoutPrefetch';

export const Footer = ({ onTrackEvent }: FooterProps) => {
  const {
    trackEventInOptimizely,
    decide,
    loading: optimizelyIsLoading,
  } = useOptimizely();

  useEffect(() => {
    if (!optimizelyIsLoading) {
      decide(Experiment.PERSISTENT_A_A);
      trackEventInOptimizely(EventName.EXAMPLE_A_A_OPTI_ONLY);
    }
  }, [optimizelyIsLoading]);

  const track = (eventName: string, customData?: Record<string, unknown>) => {
    if (isValidEvent(eventName)) {
      onTrackEvent({
        key: eventName,
        data: customData,
      });
    }
  };

  const message = '\u00A9 Checkatrade.com all rights reserved';
  const links = footerLinks.map(link => ({ ...link, title: link.label }));

  const appStore = {
    appleHref: 'https://9tuma.app.link/1ZXJxGqioQb',
    googleHref: 'https://9tuma.app.link/ySsn74AioQb',
  };

  const PopularCategories = [
    {
      url: 'https://www.checkatrade.com/Search/Electrician/in/London',
      title: 'Electricians in London',
    },
    {
      url: 'https://www.checkatrade.com/Search/Plumber/in/London',
      title: 'Plumbers in London',
    },
    {
      url: 'https://www.checkatrade.com/Search/Builder/in/London',
      title: 'Builders in London',
    },
    {
      url: 'https://www.checkatrade.com/Search/Handyman/in/London',
      title: 'Handyman in London',
    },
    {
      url: 'https://www.checkatrade.com/Search/Painter-Decorator/in/London',
      title: 'Painters and Decorators London',
    },
    {
      url: 'https://www.checkatrade.com/Search/Gas-Boiler-Servicing-Repair/in/London',
      title: 'Boiler Service in London',
    },
    {
      url: 'https://www.checkatrade.com/Search/Locksmith/in/London',
      title: 'Locksmith in London',
    },
    {
      url: 'https://www.checkatrade.com/Search/Roofer/in/Bristol',
      title: 'Roofers in Bristol',
    },
    {
      url: 'https://www.checkatrade.com/Search/Gutter-Cleaning-And-Clearance/in/Manchester',
      title: 'Gutter Cleaning in Manchester',
    },
    {
      url: 'https://www.checkatrade.com/Search/Gardener/in/Birmingham',
      title: 'Gardeners in Birmingham',
    },
    {
      url: 'https://www.checkatrade.com/Search/Damp-Proofer/in/Bristol',
      title: 'Damp Proofing in Bristol',
    },
    {
      url: 'https://www.checkatrade.com/Search/Roofing-Repairs/in/Coventry',
      title: 'Roofers in Coventry',
    },
    {
      url: 'https://www.checkatrade.com/Search/Bathroom-Fitter/in/Birmingham',
      title: 'Bathroom Fitters in Birmingham',
    },
    {
      url: 'https://www.checkatrade.com/Search/Gutter-Repair/in/Manchester',
      title: 'Gutter Repair in Manchester',
    },
    {
      url: 'https://www.checkatrade.com/Search/Water-Leak-Detection/in/Uk',
      title: 'Leak Detection Companies',
    },
    {
      url: 'https://www.checkatrade.com/Search/House-Removal/in/Birmingham',
      title: 'Removals Companies in Birmingham',
    },
    {
      url: 'https://www.checkatrade.com/Search/Washing-Machine-Repair/in/Birmingham',
      title: 'Washing Machine Repair in Birmingham',
    },
    {
      url: 'https://www.checkatrade.com/Search/Driveway-Cleaning-Patio-Cleaning/in/Manchester',
      title: 'Driveway Cleaning in Manchester',
    },
    {
      url: 'https://www.checkatrade.com/Search/Window-Cleaner/in/Bristol',
      title: 'Window Cleaner in Bristol',
    },
    {
      url: 'https://www.checkatrade.com/Search/Domestic-Cleaning/in/Woking',
      title: 'Cleaners in Woking',
    },
    {
      url: 'https://www.checkatrade.com/Search/Mobile-Mechanic/in/Southampton',
      title: 'Mobile Mechanic in Southampton',
    },
    {
      url: 'https://www.checkatrade.com/Search/Tv-Aerial-Satellite-Installation/in/Leicester',
      title: 'Aerial Installation in Leicester',
    },
    {
      url: 'https://www.checkatrade.com/Search/Carpenter/in/Birmingham',
      title: 'Carpenter in Birmingham',
    },
    {
      url: 'https://www.checkatrade.com/Search/Spray-Foam-Insulation-Removal/in/Uk',
      title: 'Spray Foam Insulation Companies',
    },
  ];
  const TradesNearMe = [
    {
      url: 'https://www.checkatrade.com/blog/plumbers-near-me/',
      title: 'Plumbers',
    },
    {
      url: 'https://www.checkatrade.com/blog/electrician-near-me/',
      title: 'Electricians',
    },
    {
      url: 'https://www.checkatrade.com/blog/roofers-near-me/',
      title: 'Roofers',
    },
    {
      url: 'https://www.checkatrade.com/blog/builders-near-me/',
      title: 'Builders',
    },
    {
      url: 'https://www.checkatrade.com/blog/bathroom-fitters-near-me/',
      title: 'Bathroom Fitters',
    },
    {
      url: 'https://www.checkatrade.com/blog/painter-decorator-near-me/',
      title: 'Painter Decorator',
    },
    {
      url: 'https://www.checkatrade.com/blog/internal-door-fitters-near-me/',
      title: 'Internal Door Fitters',
    },
    {
      url: 'https://www.checkatrade.com/blog/carpet-fitter-near-me/',
      title: 'Carpet Fitter',
    },
    {
      url: 'https://www.checkatrade.com/blog/carpenter-near-me/',
      title: 'Carpenters',
    },
    {
      url: 'https://www.checkatrade.com/blog/plasterer-near-me/',
      title: 'Plasterers',
    },
    {
      url: 'https://www.checkatrade.com/blog/locksmiths-near-me/',
      title: 'Locksmiths',
    },
    {
      url: 'https://www.checkatrade.com/blog/find-a-fencing-contractor-near-me/',
      title: 'Fencing Contractors',
    },
    {
      url: 'https://www.checkatrade.com/blog/window-cleaner-near-me/',
      title: 'Window Cleaners',
    },
    {
      url: 'https://www.checkatrade.com/blog/find-cleaners-near-me/',
      title: 'Cleaners',
    },
    {
      url: 'https://www.checkatrade.com/blog/handyman-near-me/',
      title: 'Handyman',
    },
    {
      url: 'https://www.checkatrade.com/blog/find-landscapers-near-me/',
      title: 'Landscapers',
    },
    {
      url: 'https://www.checkatrade.com/blog/tv-aerial-fitters-near-me/',
      title: 'Tv Aerial Fitters',
    },
    {
      url: 'https://www.checkatrade.com/blog/emergency-plumber-near-me/',
      title: 'Emergency Plumbers',
    },
    {
      url: 'https://www.checkatrade.com/blog/find-tilers-near-me/',
      title: 'Tilers',
    },
    {
      url: 'https://www.checkatrade.com/blog/central-heating-engineer-near-me/',
      title: 'Central Heating Engineers',
    },
    {
      url: 'https://www.checkatrade.com/blog/wardrobe-maker-near-me/',
      title: 'Wardrobe Maker',
    },
    {
      url: 'https://www.checkatrade.com/blog/man-and-van-near-me/',
      title: 'Man And Van',
    },
    {
      url: 'https://www.checkatrade.com/blog/window-fitter-near-me/',
      title: 'Window Fitter',
    },
    {
      url: 'https://www.checkatrade.com/blog/electric-cooker-installer-near-me/',
      title: 'Electric Cooker Installer',
    },
  ];
  const CostGuides = [
    {
      url: 'https://www.checkatrade.com/blog/cost-guides/painter-decorator-prices/',
      title: 'Painting and Decorating Prices',
    },
    {
      url: 'https://www.checkatrade.com/blog/cost-guides/roof-replacement-cost/',
      title: 'Roof Replacement Cost',
    },
    {
      url: 'https://www.checkatrade.com/blog/cost-guides/install-fence-panels-cost/',
      title: 'Fence Installation Cost',
    },
    {
      url: 'https://www.checkatrade.com/blog/cost-guides/new-boiler-cost/',
      title: 'New Boiler Cost',
    },
    {
      url: 'https://www.checkatrade.com/blog/cost-guides/electrical-safety-check-cost/',
      title: 'Electrical Safety Certificate Cost',
    },
    {
      url: 'https://www.checkatrade.com/blog/cost-guides/carpet-fitting-cost/',
      title: 'Carpet Fitting Cost',
    },
    {
      url: 'https://www.checkatrade.com/blog/cost-guides/house-extension-cost/',
      title: 'House Extension Cost',
    },
    {
      url: 'https://www.checkatrade.com/blog/cost-guides/skip-hire-prices/',
      title: 'Skip Hire Prices',
    },
    {
      url: 'https://www.checkatrade.com/blog/cost-guides/gutter-cleaning-cost/',
      title: 'Gutter Cleaning Cost',
    },
    {
      url: 'https://www.checkatrade.com/blog/cost-guides/patio-laying-cost/',
      title: 'Patio Laying Cost',
    },
    {
      url: 'https://www.checkatrade.com/blog/cost-guides/double-glazing-cost/',
      title: 'Double Glazed Windows Cost',
    },
    {
      url: 'https://www.checkatrade.com/blog/cost-guides/new-bathroom-cost/',
      title: 'New Bathroom Cost',
    },
  ];
  const HelpfulInfo = [
    {
      url: 'https://www.checkatrade.com/blog/hiring-guides/find-good-tradesperson/',
      title: 'Find a Tradesman',
    },
    {
      url: 'https://www.checkatrade.com/blog/hiring-guides/independent-damp-surveyor/',
      title: 'Hire a Damp Surveyor',
    },
    {
      url: 'https://www.checkatrade.com/blog/expert-advice/outside-drain-blocked-problem-solve/',
      title: 'Unblock Outside Drain',
    },
    {
      url: 'https://www.checkatrade.com/blog/hiring-guides/how-to-find-a-gardener/',
      title: 'Find a Gardener',
    },
    {
      url: 'https://www.checkatrade.com/blog/how-to/treat-damp-walls-internally/',
      title: 'Damp Walls Treatment',
    },
    {
      url: 'https://www.checkatrade.com/blog/expert-advice/boiler-leaking-water/',
      title: 'Boiler Leaking Water Issues',
    },
    {
      url: 'https://www.checkatrade.com/blog/expert-advice/conservatory-roof-options/',
      title: 'Conservatory Roof Replacement',
    },
    {
      url: 'https://www.checkatrade.com/blog/expert-advice/water-leaking-from-ceiling/',
      title: 'Water Leaking From Ceiling',
    },
    {
      url: 'https://www.checkatrade.com/blog/expert-advice/leaking-roof-repair/',
      title: 'Roof Leak Repair',
    },
    {
      url: 'https://www.checkatrade.com/check-any-tradesperson',
      title: 'Check Any Tradesperson',
    },
    {
      url: 'https://www.checkatrade.com/checkatrade-standard',
      title: 'Checkatrade Standard',
    },
    {
      url: 'https://www.checkatrade.com/reviewed-tradespeople',
      title: 'Reviewed Tradespeople',
    },
  ];

  const seoLinks = [
    { label: 'Popular Jobs', links: PopularCategories },
    { label: 'Find Tradespeople', links: TradesNearMe },
    { label: 'Cost Guides', links: CostGuides },
    { label: 'Helpful Info', links: HelpfulInfo },
  ];

  return (
    <GlobalFooter
      message={message}
      socials={socialLinks}
      links={links}
      nextLink={NextLinkWithoutPrefetch}
      appStore={appStore}
      seoLinks={seoLinks}
      track={track}
    />
  );
};

export default Footer;
